import {
  Forward,
  Inbox,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Mail as MailIcon,
  MailOpen,
  Tags,
  Reply,
  ReplyAll,
  ShieldAlert,
  Star,
  Trash2,
} from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { Tooltip } from '@/components/ui/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { MailBoxApi } from '../hooks/useMailBox'
import { GROUP_COLOR_CLASS, GROUP_DOT_CLASS } from '../mailTypes'
import { MailThreadView } from './MailThreadView'

type Props = { box: MailBoxApi }

export function MailDetail({ box }: Props) {
  const { t } = useTranslation()
  const m = box.selected
  const thread = box.threadMessages

  const replyTarget = useMemo(() => {
    if (thread.length === 0) return m
    const inbound = [...thread].reverse().find((x) => x.folder === 'INBOX' || x.from !== 'destek@urunver.com')
    return inbound ?? thread[thread.length - 1] ?? m
  }, [thread, m])

  if (!m) {
    return (
      <div className="flex h-full flex-1 flex-col items-center justify-center gap-3 bg-muted/20 px-8 text-center">
        <span className="flex size-16 items-center justify-center rounded-2xl bg-card text-primary shadow-card">
          <MailOpen className="size-7" aria-hidden />
        </span>
        <div>
          <p className="font-semibold">{t('mail.mailbox.selectPrompt')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('mail.mailbox.selectHint')}</p>
        </div>
        <p className="hidden items-center gap-1.5 text-xs text-muted-foreground md:flex">
          <Kbd>J</Kbd>
          <Kbd>K</Kbd>
          {t('mail.mailbox.shortcuts.navigateHint')}
        </p>
      </div>
    )
  }

  const index = box.filtered.findIndex((x) => x.id === m.id || x.thread_id === m.thread_id)
  const prev = index > 0 ? box.filtered[index - 1] : undefined
  const next = index >= 0 && index < box.filtered.length - 1 ? box.filtered[index + 1] : undefined
  const groups = box.groups.filter((g) => m.group_ids.includes(g.id))
  const subject = thread[0]?.subject ?? m.subject

  return (
    <article className="flex h-full min-w-0 flex-1 flex-col bg-muted/20">
      <header className="flex flex-wrap items-center gap-1 border-b border-border bg-card px-2 py-2 sm:px-3">
        <Button type="button" variant="ghost" size="icon" className="rounded-full md:hidden" aria-label={t('common.back')} onClick={box.closeMessage}>
          <ArrowLeft />
        </Button>
        <Button type="button" variant="ghost" size="sm" className="rounded-full" onClick={() => replyTarget && box.openCompose('reply', replyTarget)}>
          <Reply />
          <span className="hidden sm:inline">{t('mail.mailbox.actions.reply')}</span>
        </Button>
        <Button type="button" variant="ghost" size="sm" className="rounded-full" onClick={() => replyTarget && box.openCompose('replyAll', replyTarget)}>
          <ReplyAll />
          <span className="hidden sm:inline">{t('mail.mailbox.actions.replyAll')}</span>
        </Button>
        <Button type="button" variant="ghost" size="sm" className="rounded-full" onClick={() => box.openCompose('forward', m)}>
          <Forward />
          <span className="hidden sm:inline">{t('mail.mailbox.actions.forward')}</span>
        </Button>
        <div className="mx-1 hidden h-5 w-px bg-border sm:block" />
        <Tooltip content={t('mail.mailbox.bulk.unread')}>
          <Button type="button" variant="ghost" size="icon" className="rounded-full" aria-label={t('mail.mailbox.bulk.unread')} onClick={() => box.setRead([m.id], false)}>
            <MailIcon />
          </Button>
        </Tooltip>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label={m.is_favorite ? t('mail.mailbox.unstar') : t('mail.mailbox.star')}
          onClick={() => box.toggleFavorite(m.id)}
        >
          <Star className={cn(m.is_favorite && 'fill-warning text-warning')} />
        </Button>
        {m.folder === 'SPAM' ? (
          <Button type="button" variant="ghost" size="icon" className="rounded-full" aria-label={t('mail.mailbox.actions.notSpam')} onClick={() => box.moveToFolder(m.id, 'INBOX')}>
            <Inbox />
          </Button>
        ) : (
          <Button type="button" variant="ghost" size="icon" className="rounded-full" aria-label={t('mail.mailbox.actions.spam')} onClick={() => box.moveToFolder(m.id, 'SPAM')}>
            <ShieldAlert />
          </Button>
        )}
        {m.folder === 'TRASH' ? (
          <Button type="button" variant="ghost" size="icon" className="rounded-full text-destructive" aria-label={t('mail.mailbox.actions.deleteForever')} onClick={() => void box.deletePermanent(m.id)}>
            <Trash2 />
          </Button>
        ) : (
          <Button type="button" variant="ghost" size="icon" className="rounded-full" aria-label={t('mail.mailbox.actions.trash')} onClick={() => box.moveToFolder(m.id, 'TRASH')}>
            <Trash2 />
          </Button>
        )}
        {m.folder === 'DRAFT' ? (
          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => box.openCompose('draft', m)}>
            {t('mail.mailbox.actions.editDraft')}
          </Button>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="rounded-full" aria-label={t('mail.mailbox.groups.assign')}>
              <Tags />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {box.groups.length === 0 ? (
              <DropdownMenuItem disabled>{t('mail.mailbox.groups.empty')}</DropdownMenuItem>
            ) : (
              box.groups.map((g) => {
                const on = m.group_ids.includes(g.id)
                return (
                  <DropdownMenuItem key={g.id} onSelect={() => box.toggleGroupOnMessage(m.id, g.id)}>
                    <span className={cn('mr-2 size-2 rounded-full', GROUP_DOT_CLASS[g.colorToken])} />
                    {on ? '✓ ' : ''}
                    {g.name}
                  </DropdownMenuItem>
                )
              })
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {index >= 0 ? (
          <div className="ml-auto flex items-center gap-0.5">
            <span className="mr-1 hidden text-xs tabular-nums text-muted-foreground sm:inline">{t('mail.mailbox.position', { index: index + 1, total: box.filtered.length })}</span>
            <Button type="button" variant="ghost" size="icon" className="size-8 rounded-full" disabled={!prev} aria-label={t('mail.mailbox.prev')} onClick={() => prev && box.selectMessage(prev.id)}>
              <ChevronLeft />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="size-8 rounded-full" disabled={!next} aria-label={t('mail.mailbox.next')} onClick={() => next && box.selectMessage(next.id)}>
              <ChevronRight />
            </Button>
          </div>
        ) : null}
      </header>

      <div className="flex-1 overflow-y-auto scrollbar-none">
        <div className="mx-auto w-full p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <h1 className="text-xl font-semibold leading-snug tracking-tight text-foreground sm:text-2xl">
              {subject}
            </h1>
            {thread.length > 1 ? (
              <span className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                {t('mail.mailbox.threadCount', { count: thread.length })}
              </span>
            ) : null}
          </div>
          {groups.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {groups.map((g) => (
                <span key={g.id} className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', GROUP_COLOR_CLASS[g.colorToken])}>
                  {g.name}
                </span>
              ))}
            </div>
          ) : null}
          <MailThreadView
            messages={thread.length > 0 ? thread : [m]}
            threadKey={m.thread_id}
            onToggleFavorite={box.toggleFavorite}
          />
        </div>
      </div>
    </article>
  )
}
